import api from '../services/api'

/**
 * Download the full record of a project: every record and every document, in one file.
 *
 * Shared by the closure screen and the project header. Two copies of this would drift, and the
 * first thing to drift would be the part that reports unreadable files — which is the only part
 * standing between a hollow archive and somebody believing they have a complete one.
 *
 * Resolves with { included, missing } and throws with a readable message. It does not decide how
 * to tell anybody: each screen says it in its own way.
 */
export async function downloadProjectExport(projectId) {
  let res
  try {
    res = await api.get(`/projects/${projectId}/export`, { responseType: 'blob' })
  } catch (e) {
    // The failure body is a Blob too, because of responseType above. Reading .detail off it
    // gives undefined, which is how somebody ends up being told nothing at all went wrong.
    let detail = ''
    try {
      detail = JSON.parse(await e.response?.data?.text?.() || '{}')?.detail || ''
    } catch { /* not JSON — fall through to the general message */ }
    throw new Error(detail || 'The export could not be built. Nothing has been changed.')
  }

  // The server names the file, because the name carries the project number and the date. A
  // browser default would not.
  const cd = res.headers?.['content-disposition'] || ''
  const match = /filename="?([^";]+)"?/.exec(cd)

  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = match ? match[1] : `project-${projectId}-export.zip`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)

  return {
    included: Number(res.headers?.['x-export-files'] || 0),
    missing: Number(res.headers?.['x-export-files-missing'] || 0),
  }
}

/** The same sentence on every screen, so one cannot sound safer than another. */
export function exportMessage({ included, missing }) {
  if (missing > 0) {
    return {
      bad: true,
      text: `Downloaded with ${included} file(s), but ${missing} could not be read from storage. `
          + 'They are named in the archive rather than left out silently — check manifest.json '
          + 'before handing this on.',
    }
  }
  return { bad: false, text: `Downloaded — every record and ${included} file(s).` }
}
